"use client";

import classNames from "classnames";
import { useState } from "react";

import { CountriesFlagsDropdown } from "@/components";
import { COUNTRIES_DETAILS } from "@/constants";
import { setFieldValue } from "@/stores";

import { PhoneNumberInputTypes } from "./PhoneNumberInput.types";

import styles from "./PhoneNumberInput.module.scss";

/**
 * PhoneNumberInput Component
 *
 * Phone field with a country-prefix picker and digit-only typing.
 *
 * @returns Labeled phone input row
 */
const PhoneNumberInput = ({
  onChange,
  label,
  placeholder,
  defaultValue = "",
  required = false,
  variant = "default",
  id = "",
  size = "medium",
  disabled = false,
  onKeyDown = () => {},
}: PhoneNumberInputTypes) => {
  // Prefix shown beside the number; starts on the catalog’s default country
  const [prefix, setPrefix] = useState(COUNTRIES_DETAILS[173].phone[0]);

  // Block non-digit keys so the value stays a phone number
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/^[0-9, ]$/.test(e.key) &&
      !["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(e.key)
    ) {
      e.preventDefault();
    }

    onKeyDown(e);
  };

  // Forward the typed digits to the parent
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onChange(value);
  };

  // Keep the visible prefix and the form store in sync when the flag changes
  const onChangePhonePrefix = (item: any) => {
    setPrefix(item.phone[0]);
    setFieldValue("phonePrefix", item.phone[0]);
  };

  return (
    <div className={styles["input"]}>
      {label && (
        <label
          className={classNames(
            styles.input__label,
            styles[`input__label--${variant}`]
          )}
        >
          <span>
            {label} {required && "*"}
          </span>
        </label>
      )}

      <div
        className={classNames(
          styles["input__field"],
          styles[`input__field--size-${size}`],
          styles[`input__field--${variant}`],
          disabled && styles["input__field--disabled"]
        )}
      >
        {/* Flag menu that updates the dialing prefix */}
        <CountriesFlagsDropdown onChange={onChangePhonePrefix} />

        <div className={styles["input__field__prefix"]}>{prefix}</div>

        <input
          id={id}
          disabled={disabled}
          defaultValue={defaultValue}
          onChange={handleChange}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onWheel={(e: any) => e.target.blur()}
        />
      </div>
    </div>
  );
};

export { PhoneNumberInput };
